import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Switch from "@mui/material/Switch";
import styles from "./styles.module.scss";
import ClientIdLabel from "./ClientIdLabel";
import AutoConsentDecisionModal, {
  AutoConsentDecision,
} from "../../../../../components/organisations/AutoConsentDecisionModal";
import { canAct } from "../../../../../setup/auth/oidc";
import { IOrganizations } from "../../../../../setup/types/Organisation";
import Spinner from "../../../../../components/common/Spinner";
import BusinessTable, {
  RowWithOptionalClick,
} from "../../../../../components/table";
import Pagination from "../../../../../components/pagination";
import {
  approveOrganizationRequest,
  rejectOrganizationRequest,
  getRequestErrorMessage,
  createOrganizationClient,
  getOrganizationClientAudit,
  getOrganizationClients,
  setOrganizationClientAutoAcceptConsent,
  setOrganizationClientCheckoutLoginMode,
  setOrganizationClientEnabled,
  setOrganizationClientConsentSendHourlyLimit,
  setOrganizationClientOtpSendHourlyLimit,
} from "../../../../../setup/api/services/organization";
import {
  CheckoutLoginMode,
  IOrgClient,
  IOrgClientAudit,
  IOrgClientCreated,
  OrgClientAuditActorType,
  OrgClientAuditEventType,
} from "../../../../../setup/api/services/organization/models";
import ReasonPromptModal from "./ReasonPromptModal";
import OtpLimitPromptModal from "./OtpLimitPromptModal";
import CheckoutLoginModePromptModal from "./CheckoutLoginModePromptModal";
import CreateClientPromptModal from "./CreateClientPromptModal";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import ClientCredentialsModal from "../../../../../components/organisations/ClientCredentialsModal/ClientCredentialsModal";

interface ClientsInformationProps {
  organization: IOrganizations | undefined;
}

/**
 * BusinessTable's default export is React.memo(...)-wrapped, which erases its generic type
 * parameter — so the row type must nominally extend RowWithOptionalClick (matching
 * UsersInformation's identical pattern) rather than relying on structural compatibility alone;
 * TS's "weak type" check otherwise rejects passing IOrgClient[]/IOrgClientAudit[] directly.
 */
interface ClientRow extends RowWithOptionalClick, IOrgClient {}
interface AuditRow extends RowWithOptionalClick, IOrgClientAudit {}

/**
 * Staff-only client-management view (design doc §9 Phase 2): every KC client for this org —
 * self-service or staff-added — with enable/disable, auto-accept-consent, and otp-send-hourly-limit
 * (§6.5) control, plus the audit trail those controls (and self-service creation) write to.
 */
const ClientsInformation = ({ organization }: ClientsInformationProps) => {
  const { t } = useTranslation();

  const EVENT_LABELS: Record<OrgClientAuditEventType, string> = {
    CREATED: t("lbl_audit_event_created"),
    ENABLED: t("lbl_audit_event_enabled"),
    DISABLED: t("lbl_audit_event_disabled"),
    AUTO_ACCEPT_CONSENT_CHANGED: t(
      "lbl_audit_event_auto_accept_consent_changed"
    ),
    OTP_SEND_HOURLY_LIMIT_CHANGED: t(
      "lbl_audit_event_otp_send_hourly_limit_changed"
    ),
    CONSENT_SEND_HOURLY_LIMIT_CHANGED: t(
      "lbl_audit_event_consent_send_hourly_limit_changed"
    ),
    AUTO_ACCEPT_CONSENT_REQUESTED: t(
      "lbl_audit_event_auto_accept_consent_requested"
    ),
    AUTO_ACCEPT_CONSENT_REQUEST_REJECTED: t(
      "lbl_audit_event_auto_accept_consent_request_rejected"
    ),
    AUTO_ACCEPT_CONSENT_REQUEST_CANCELLED: t(
      "lbl_audit_event_auto_accept_consent_request_cancelled"
    ),
    CHECKOUT_LOGIN_MODE_CHANGED: t(
      "lbl_audit_event_checkout_login_mode_changed"
    ),
  };

  const ACTOR_LABELS: Record<OrgClientAuditActorType, string> = {
    SELF_SERVICE: t("lbl_audit_actor_self_service"),
    STAFF: t("lbl_audit_actor_staff"),
  };

  const clientColumns = [
    { type: "clientId", value: t("lbl_client_id") },
    { type: "description", value: t("description") },
    { type: "status", value: t("lbl_status") },
    { type: "consent", value: t("lbl_auto_accept_consent") },
    { type: "otpLimit", value: t("lbl_otp_send_hourly_limit") },
    { type: "consentLimit", value: t("lbl_consent_send_hourly_limit") },
    { type: "checkoutLogin", value: t("lbl_checkout_login_mode") },
  ];

  const auditColumns = [
    { type: "clientId", value: t("lbl_audit_client") },
    { type: "eventType", value: t("lbl_audit_event") },
    { type: "actorType", value: t("lbl_audit_actor") },
    { type: "reason", value: t("lbl_audit_reason") },
    { type: "createdAt", value: t("lbl_audit_created_at") },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [savingClientId, setSavingClientId] = useState<string | null>(null);

  const [pendingConsentClientId, setPendingConsentClientId] = useState<
    string | null
  >(null);
  const [pendingConsentTarget, setPendingConsentTarget] = useState(false);

  const [pendingOtpLimitClientId, setPendingOtpLimitClientId] = useState<
    string | null
  >(null);
  const [pendingConsentLimitClientId, setPendingConsentLimitClientId] =
    useState<string | null>(null);
  const [pendingCheckoutModeClientId, setPendingCheckoutModeClientId] =
    useState<string | null>(null);

  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPagesCount, setAuditPagesCount] = useState(1);
  const [auditTotalCount, setAuditTotalCount] = useState(0);
  const auditPageSize = 10;

  const [creatingClient, setCreatingClient] = useState(false);
  const [showCreateClientPrompt, setShowCreateClientPrompt] = useState(false);
  const [createdCredentials, setCreatedCredentials] =
    useState<IOrgClientCreated | null>(null);

  // A request link from the auto-consent requests tab carries ?client=<id>.
  const [searchParams] = useSearchParams();
  const highlightedClientId = searchParams.get("client");
  const mayDecide = canAct();
  const [pendingDecision, setPendingDecision] = useState<{
    clientId: string;
    requestId: number;
    decision: AutoConsentDecision;
  } | null>(null);

  const fetchClients = useCallback(async () => {
    if (!organization?.code) return;
    setIsLoading(true);
    try {
      const response = await getOrganizationClients(organization.code);
      setClients(response?.data ?? []);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [organization?.code]);

  const fetchAudit = useCallback(async () => {
    if (!organization?.code) return;
    setAuditLoading(true);
    try {
      const response = await getOrganizationClientAudit(
        organization.code,
        auditPage - 1,
        auditPageSize
      );
      setAuditRows(response?.data?.content ?? []);
      setAuditPagesCount(response?.data?.totalPages ?? 1);
      setAuditTotalCount(response?.data?.totalElements ?? 0);
    } catch (error) {
      console.error(error);
    } finally {
      setAuditLoading(false);
    }
  }, [organization?.code, auditPage]);

  const handleConfirmCreateClient = useCallback(
    async (description: string) => {
      if (!organization?.code) return;
      setShowCreateClientPrompt(false);
      setCreatingClient(true);
      try {
        const response = await createOrganizationClient(
          organization.code,
          description || undefined
        );
        setCreatedCredentials(response?.data ?? null);
        fetchClients();
        fetchAudit();
      } catch (error) {
        console.error(error);
        toast.error(t("lbl_create_client_failed"));
      } finally {
        setCreatingClient(false);
      }
    },
    [organization?.code, fetchClients, fetchAudit, t]
  );

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const handleToggleEnabled = async (
    clientId: string,
    nextEnabled: boolean
  ) => {
    if (!organization?.code) return;
    setSavingClientId(clientId);
    try {
      await setOrganizationClientEnabled(
        organization.code,
        clientId,
        nextEnabled
      );
      setClients((prev) =>
        prev.map((c) =>
          c.clientId === clientId ? { ...c, enabled: nextEnabled } : c
        )
      );
      fetchAudit();
    } catch (error) {
      console.error(error);
    } finally {
      setSavingClientId(null);
    }
  };

  const handleToggleConsentRequested = (
    clientId: string,
    nextValue: boolean
  ) => {
    setPendingConsentClientId(clientId);
    setPendingConsentTarget(nextValue);
  };

  const handleConfirmConsentChange = async (reason: string) => {
    if (!organization?.code || !pendingConsentClientId) return;
    const clientId = pendingConsentClientId;
    const nextValue = pendingConsentTarget;
    setPendingConsentClientId(null);
    setSavingClientId(clientId);
    try {
      await setOrganizationClientAutoAcceptConsent(
        organization.code,
        clientId,
        nextValue,
        reason
      );
      setClients((prev) =>
        prev.map((c) =>
          c.clientId === clientId
            ? {
                ...c,
                autoAcceptConsent: nextValue,
                // Turning it on settles the client's open request as approved.
                autoAcceptConsentRequest:
                  nextValue && c.autoAcceptConsentRequest?.status === "PENDING"
                    ? { ...c.autoAcceptConsentRequest, status: "APPROVED" }
                    : c.autoAcceptConsentRequest,
              }
            : c
        )
      );
      fetchAudit();
    } catch (error) {
      console.error(error);
    } finally {
      setSavingClientId(null);
    }
  };

  const handleConfirmOtpLimitChange = async (
    otpSendHourlyLimit: number | null,
    reason: string
  ) => {
    if (!organization?.code || !pendingOtpLimitClientId) return;
    const clientId = pendingOtpLimitClientId;
    setPendingOtpLimitClientId(null);
    setSavingClientId(clientId);
    try {
      await setOrganizationClientOtpSendHourlyLimit(
        organization.code,
        clientId,
        otpSendHourlyLimit,
        reason
      );
      setClients((prev) =>
        prev.map((c) =>
          c.clientId === clientId ? { ...c, otpSendHourlyLimit } : c
        )
      );
      fetchAudit();
    } catch (error) {
      console.error(error);
    } finally {
      setSavingClientId(null);
    }
  };

  const handleConfirmConsentLimitChange = async (
    consentSendHourlyLimit: number | null,
    reason: string
  ) => {
    if (!organization?.code || !pendingConsentLimitClientId) return;
    const clientId = pendingConsentLimitClientId;
    setPendingConsentLimitClientId(null);
    setSavingClientId(clientId);
    try {
      await setOrganizationClientConsentSendHourlyLimit(
        organization.code,
        clientId,
        consentSendHourlyLimit,
        reason
      );
      setClients((prev) =>
        prev.map((c) =>
          c.clientId === clientId ? { ...c, consentSendHourlyLimit } : c
        )
      );
      fetchAudit();
    } catch (error) {
      console.error(error);
    } finally {
      setSavingClientId(null);
    }
  };

  const handleConfirmCheckoutModeChange = async (
    checkoutLoginMode: CheckoutLoginMode,
    reason: string
  ) => {
    if (!organization?.code || !pendingCheckoutModeClientId) return;
    const clientId = pendingCheckoutModeClientId;
    setPendingCheckoutModeClientId(null);
    setSavingClientId(clientId);
    try {
      await setOrganizationClientCheckoutLoginMode(
        organization.code,
        clientId,
        checkoutLoginMode,
        reason
      );
      setClients((prev) =>
        prev.map((c) =>
          c.clientId === clientId ? { ...c, checkoutLoginMode } : c
        )
      );
      fetchAudit();
    } catch (error) {
      console.error(error);
      toast.error(t("lbl_checkout_login_mode_update_failed"));
    } finally {
      setSavingClientId(null);
    }
  };

  const handleConfirmDecision = async (reason: string) => {
    if (!pendingDecision) return;
    const { requestId, decision } = pendingDecision;
    setPendingDecision(null);
    try {
      if (decision === "approve") {
        const response = await approveOrganizationRequest(requestId, reason);
        toast.success(
          t("lbl_auto_consent_approved_toast", {
            count: response?.data?.approvedLinks ?? 0,
          })
        );
      } else {
        await rejectOrganizationRequest(requestId, reason);
        toast.success(t("lbl_auto_consent_rejected_toast"));
      }
      fetchClients();
      fetchAudit();
    } catch (error) {
      console.error(error);
      // The request may already be decided or cancelled elsewhere; say why and show its real state
      toast.error(
        getRequestErrorMessage(error) ?? t("lbl_auto_consent_decision_failed")
      );
      fetchClients();
      fetchAudit();
    }
  };

  const clientsWithPendingRequest = clients.filter(
    (c) => c.autoAcceptConsentRequest?.status === "PENDING"
  );

  const renderCell = (columnType: string, row: ClientRow) => {
    switch (columnType) {
      case "clientId":
        return (
          <ClientIdLabel
            clientId={row.clientId}
            highlighted={row.clientId === highlightedClientId}
          />
        );
      case "description":
        return (
          <span className={styles.nameValue}>{row.description || "—"}</span>
        );
      case "status":
        return (
          <Switch
            checked={row.enabled}
            disabled={savingClientId === row.clientId}
            onChange={(_, checked) =>
              handleToggleEnabled(row.clientId, checked)
            }
          />
        );
      case "consent": {
        const request = row.autoAcceptConsentRequest;
        return (
          <div className={styles.consentCell}>
            <Switch
              checked={row.autoAcceptConsent}
              disabled={savingClientId === row.clientId}
              onChange={(_, checked) =>
                handleToggleConsentRequested(row.clientId, checked)
              }
            />
            {request?.status === "PENDING" && (
              <div className={styles.requestPending}>
                <span className={styles.requestChip} title={request.reason}>
                  {t("lbl_auto_consent_request_pending")}
                </span>
                {mayDecide && (
                  <>
                    <button
                      type="button"
                      className={styles.editLink}
                      onClick={() =>
                        setPendingDecision({
                          clientId: row.clientId,
                          requestId: request.id,
                          decision: "approve",
                        })
                      }>
                      {t("lbl_auto_consent_approve")}
                    </button>
                    <button
                      type="button"
                      className={`${styles.editLink} ${styles.dangerLink}`}
                      onClick={() =>
                        setPendingDecision({
                          clientId: row.clientId,
                          requestId: request.id,
                          decision: "reject",
                        })
                      }>
                      {t("lbl_auto_consent_reject")}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        );
      }
      case "otpLimit":
        return (
          <div className={styles.otpLimitCell}>
            <span
              className={
                row.otpSendHourlyLimit == null
                  ? `${styles.otpLimitValue} ${styles.otpLimitDefault}`
                  : styles.otpLimitValue
              }>
              {row.otpSendHourlyLimit ??
                (row.otpSendHourlyLimitDefault != null
                  ? t("lbl_limit_default_with_value", {
                      value: row.otpSendHourlyLimitDefault,
                    })
                  : t("lbl_otp_send_hourly_limit_default"))}
            </span>
            <button
              type="button"
              className={styles.editLink}
              disabled={savingClientId === row.clientId}
              onClick={() => setPendingOtpLimitClientId(row.clientId)}>
              {t("lbl_edit")}
            </button>
          </div>
        );
      case "consentLimit":
        return (
          <div className={styles.otpLimitCell}>
            <span
              className={
                row.consentSendHourlyLimit == null
                  ? `${styles.otpLimitValue} ${styles.otpLimitDefault}`
                  : styles.otpLimitValue
              }>
              {row.consentSendHourlyLimit ??
                (row.consentSendHourlyLimitDefault != null
                  ? t("lbl_limit_default_with_value", {
                      value: row.consentSendHourlyLimitDefault,
                    })
                  : t("lbl_otp_send_hourly_limit_default"))}
            </span>
            <button
              type="button"
              className={styles.editLink}
              disabled={savingClientId === row.clientId}
              onClick={() => setPendingConsentLimitClientId(row.clientId)}>
              {t("lbl_edit")}
            </button>
          </div>
        );
      case "checkoutLogin":
        return (
          <div className={styles.otpLimitCell}>
            <span className={styles.otpLimitValue}>
              {t(`lbl_checkout_login_mode_${row.checkoutLoginMode ?? "any"}`)}
            </span>
            <button
              type="button"
              className={styles.editLink}
              disabled={savingClientId === row.clientId}
              onClick={() => setPendingCheckoutModeClientId(row.clientId)}>
              {t("lbl_edit")}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const renderAuditCell = (columnType: string, row: AuditRow) => {
    switch (columnType) {
      case "clientId":
        return (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {row.clientId}
          </span>
        );
      case "eventType":
        return (
          <span className={styles.nameValue}>
            {EVENT_LABELS[row.eventType] ?? row.eventType}
          </span>
        );
      case "actorType":
        return (
          <span className={styles.nameValue}>
            {ACTOR_LABELS[row.actorType] ?? row.actorType}
          </span>
        );
      case "reason":
        return <span className={styles.nameValue}>{row.reason || "—"}</span>;
      case "createdAt":
        return (
          <span className={styles.nameValue} style={{ direction: "ltr" }}>
            {new Date(row.createdAt).toLocaleString("ar-SA")}
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div>
          <h3 className={styles.title}>{t("lbl_api_clients")}</h3>
          <p className={styles.subtitle}>{t("lbl_api_clients_subtitle")}</p>
        </div>
        <PrimaryButton
          onClick={() => setShowCreateClientPrompt(true)}
          disabled={creatingClient}
          width="auto">
          {creatingClient ? <Spinner /> : t("lbl_create_client")}
        </PrimaryButton>
      </div>
      {clientsWithPendingRequest.length > 0 && (
        <div className={styles.requestBanner}>
          {t("lbl_auto_consent_banner", {
            count: clientsWithPendingRequest.length,
          })}
        </div>
      )}
      <div className={styles.tableContainer}>
        <div className={styles.clientsTableWrapper}>
          <BusinessTable
            columns={clientColumns}
            rows={clients}
            renderCell={(column, row) => renderCell(column, row as ClientRow)}
          />
        </div>
      </div>

      <div className={styles.auditSection}>
        <h4 className={styles.auditTitle}>{t("lbl_audit_log")}</h4>
        {auditLoading ? (
          <div className={styles.spinnerContainer}>
            <Spinner />
          </div>
        ) : (
          <>
            <div className={styles.auditTableWrapper}>
              <BusinessTable
                columns={auditColumns}
                rows={auditRows}
                renderCell={(column, row) =>
                  renderAuditCell(column, row as AuditRow)
                }
              />
            </div>
            <Pagination
              countName={t("lbl_audit_log_row_name")}
              pagesCount={auditPagesCount}
              page={auditPage}
              pageSize={auditPageSize}
              length={auditTotalCount}
              handlePaginationChange={(page) => setAuditPage(page)}
            />
          </>
        )}
      </div>

      <ReasonPromptModal
        open={!!pendingConsentClientId}
        title={
          pendingConsentTarget
            ? t("lbl_grant_auto_accept_consent_title")
            : t("lbl_revoke_auto_accept_consent_title")
        }
        notice={
          pendingConsentTarget
            ? t("lbl_grant_auto_accept_consent_notice")
            : t("lbl_revoke_auto_accept_consent_notice")
        }
        handleClose={() => setPendingConsentClientId(null)}
        handleConfirm={handleConfirmConsentChange}
      />

      <OtpLimitPromptModal
        open={!!pendingOtpLimitClientId}
        currentValue={
          clients.find((c) => c.clientId === pendingOtpLimitClientId)
            ?.otpSendHourlyLimit
        }
        handleClose={() => setPendingOtpLimitClientId(null)}
        handleConfirm={handleConfirmOtpLimitChange}
      />

      <OtpLimitPromptModal
        open={!!pendingConsentLimitClientId}
        titleKey="lbl_edit_consent_send_hourly_limit_title"
        subtitleKey="lbl_consent_send_hourly_limit_subtitle"
        currentValue={
          clients.find((c) => c.clientId === pendingConsentLimitClientId)
            ?.consentSendHourlyLimit
        }
        handleClose={() => setPendingConsentLimitClientId(null)}
        handleConfirm={handleConfirmConsentLimitChange}
      />

      <AutoConsentDecisionModal
        open={pendingDecision !== null}
        decision={pendingDecision?.decision ?? "approve"}
        subtitle={pendingDecision?.clientId}
        handleClose={() => setPendingDecision(null)}
        handleConfirm={handleConfirmDecision}
      />

      <CheckoutLoginModePromptModal
        open={!!pendingCheckoutModeClientId}
        currentValue={
          clients.find((c) => c.clientId === pendingCheckoutModeClientId)
            ?.checkoutLoginMode
        }
        handleClose={() => setPendingCheckoutModeClientId(null)}
        handleConfirm={handleConfirmCheckoutModeChange}
      />

      <CreateClientPromptModal
        open={showCreateClientPrompt}
        handleClose={() => setShowCreateClientPrompt(false)}
        handleConfirm={handleConfirmCreateClient}
      />

      <ClientCredentialsModal
        open={!!createdCredentials}
        credentials={createdCredentials}
        handleClose={() => setCreatedCredentials(null)}
      />
    </div>
  );
};

export default ClientsInformation;
